// Data for the Infrastructure pages: live lists through useKList (list then watch), subresource reads through
// TanStack Query, and the writes the pages make. Every write goes through the same API a CLI call would use.
import { collectionPath, newIdempotencyKey, objectPath, queryKeys, subresourcePath, useApi, useKList, type Scope } from "@nodus/api";
import { toast } from "@nodus/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";
import {
  DRAIN_ANNOTATION,
  type CloudAccount,
  type DismissReason,
  type EnrollmentToken,
  type Pool,
  type PoolAction,
  type PoolNode,
  type PoolSpec,
} from "./types";

export interface ProjectScope {
  org: string;
  project: string;
}

export function usePools(scope: ProjectScope) {
  return useKList<Pool>("Pool", scope);
}

export function usePool(scope: ProjectScope, name: string) {
  const list = useKList<Pool>("Pool", scope, { fieldSelector: `metadata.name=${name}` });
  const pool = list.data?.items.find((p) => p.metadata.name === name);
  return { ...list, pool, notFound: list.isSuccess && !pool };
}

/**
 * Heartbeats and token use change a Node's or EnrollmentToken's status with no change row, so those lists are also
 * listed again on this interval, well inside the 10-minute offline window.
 */
const RELIST_MS = 15_000;

/** Nodes are org-scoped; a pool's hosts are the Nodes whose status names it. */
export function useNodes(org: string, pool?: string) {
  const list = useKList<PoolNode>("Node", { org }, { relistEvery: RELIST_MS });
  const items = React.useMemo(
    () => (list.data?.items ?? []).filter((n) => !pool || n.status?.pool === pool),
    [list.data, pool],
  );
  return { ...list, items };
}

export function useTokens(scope: ProjectScope, pool: string) {
  const list = useKList<EnrollmentToken>("EnrollmentToken", scope, { relistEvery: RELIST_MS });
  return { ...list, items: (list.data?.items ?? []).filter((t) => t.spec?.pool === pool) };
}

export function usePoolActions(scope: ProjectScope, pool?: string) {
  const list = useKList<PoolAction>("PoolAction", scope, pool ? { labelSelector: `nodus.dev/pool=${pool}` } : {});
  return { ...list, items: list.data?.items ?? [] };
}

export function useCloudAccounts(org: string) {
  const list = useKList<CloudAccount>("CloudAccount", { org });
  return { ...list, items: list.data?.items ?? [] };
}

/** A subresource read (utilization, forecast, recommendations, onboarding, inventory); no watch. */
export function useSubresource<T>(kind: string, name: string, sub: string, scope: Scope, query: Record<string, string> = {}, enabled = true) {
  const { client } = useApi();
  const path = subresourcePath(kind, name, sub, scope.project);
  return useQuery({
    queryKey: [...queryKeys.path(scope, path), query],
    enabled,
    queryFn: () => client.get<T>(path, { org: scope.org, query }),
    retry: false,
  });
}

async function attempt<T>(what: string, fn: () => Promise<T>): Promise<T | undefined> {
  try {
    return await fn();
  } catch (e) {
    toast.error(`Could not ${what}.`, { description: (e as { message?: string }).message });
    return undefined;
  }
}

/** The writes of the Infrastructure pages, bound to one project. */
export function usePoolWrites(scope: ProjectScope) {
  const { client } = useApi();
  return React.useMemo(() => {
    const opts = { org: scope.org };
    return {
      createPool: (name: string, spec: PoolSpec, labels: Record<string, string>, annotations: Record<string, string>) =>
        attempt("create the pool", () =>
          client.post<Pool>(
            collectionPath("Pool", scope.project),
            { apiVersion: "nodus.dev/v1", kind: "Pool", metadata: { name, labels, annotations }, spec },
            opts,
          ),
        ),
      /** A merge patch of spec; annotations carry a price consent on the update that needs one. */
      patchPool: (name: string, spec: Partial<PoolSpec>, annotations?: Record<string, string>) =>
        attempt("update the pool", () =>
          client.patch<Pool>(objectPath("Pool", name, scope.project), { spec, ...(annotations ? { metadata: { annotations } } : {}) }, opts),
        ),
      deletePool: (name: string) => attempt("delete the pool", () => client.delete(objectPath("Pool", name, scope.project), opts)),
      createToken: (pool: string, labels?: Record<string, string>) =>
        attempt("create the enrollment token", () =>
          client.post<EnrollmentToken>(
            collectionPath("EnrollmentToken", scope.project),
            { apiVersion: "nodus.dev/v1", kind: "EnrollmentToken", metadata: { generateName: `${pool}-` }, spec: { pool, ...(labels ? { labels } : {}) } },
            opts,
          ),
        ),
      revokeToken: (name: string) =>
        attempt("revoke the token", () => client.delete(objectPath("EnrollmentToken", name, scope.project), opts)),
      decide: (action: string, decision: "Approved" | "Rejected") =>
        attempt(decision === "Approved" ? "approve the action" : "reject the action", () =>
          client.patch(objectPath("PoolAction", action, scope.project), { spec: { decision } }, opts),
        ),
      revert: (action: string) =>
        attempt("undo the action", () =>
          client.patch(objectPath("PoolAction", action, scope.project), {
            metadata: { annotations: { "nodus.dev/revert-requested-at": new Date().toISOString() } },
          }, opts),
        ),
      /** POST recommendations/{id}/dismissals records the dismissal with one of the server's reasons. */
      dismiss: (pool: string, id: string, reason: DismissReason) =>
        attempt("dismiss the recommendation", () =>
          client.post(`${subresourcePath("Pool", pool, "recommendations", scope.project, id)}/dismissals`, { reason }, {
            ...opts,
            idempotencyKey: newIdempotencyKey(),
          }),
        ),
    };
  }, [client, scope.org, scope.project]);
}

/**
 * Node verbs: the drain annotation present drains the host and removing it (a merge-patch null) undrains it;
 * unenroll is DELETE (drain, then revoke).
 */
export function useNodeWrites(org: string) {
  const { client } = useApi();
  return React.useMemo(
    () => ({
      setDrain: (node: string, drain: boolean) =>
        attempt(drain ? "drain the node" : "undrain the node", () =>
          client.patch(objectPath("Node", node), {
            metadata: { annotations: { [DRAIN_ANNOTATION]: drain ? new Date().toISOString() : null } },
          }, { org }),
        ),
      unenroll: (node: string) => attempt("unenroll the node", () => client.delete(objectPath("Node", node), { org })),
    }),
    [client, org],
  );
}

/** A clock that ticks every `ms` for countdowns and "last heartbeat" ages. */
export function useNow(ms = 30_000): Date {
  const [now, setNow] = React.useState(() => new Date());
  React.useEffect(() => {
    const t = setInterval(() => setNow(new Date()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}
